import { useMutation } from "@apollo/client";
import Download from "@mui/icons-material/Download";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Button,
  Collapse,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tooltip,
  Typography,
  styled,
  useTheme,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { EncampyPortalLoginLauncher } from "components/Encampy/PortalLoginLauncher";
import { GeneratePdfButton } from "components/GeneratePdfButton";
import CopyableTypography from "components/Typography/CopyableTypography";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import { TierIiReportOrgStatus } from "generated-graphql/graphql";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { useCallback, useState } from "react";
import { CredentialVerification } from "./CredentialVerification";
import { FieldDisplay } from "./shared/FieldComponents";
import { useFulfillmentDetails } from "./useFulfillmentDetails";

const mutation = gql(`
  mutation GenerateT2S($reportId: ID!) {
    generateT2S(reportId: $reportId)
  }
`);

const CredentialsSection = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

const VerificationSection = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

export function StatePortal() {
  const theme = useTheme();
  const alerts = useAlerts();
  const { data } = useFulfillmentDetails();
  const [downloadMenuAnchor, setDownloadMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [isExpanded, setIsExpanded] = useLocalStorageState<boolean>(
    "fulfillment-state-portal-expanded",
    { defaultValue: true }
  );

  const portalCredential = data?.tierIIReport.facility.portalCredential;
  const facilityAlternateIds = data?.tierIIReport.facility.facilityAlternateIds;
  const reportingYear = data?.tierIIReport.reportingYear;
  const reportKind = data?.tierIIReport.reportKind;

  const [mutate, { loading: isGeneratingT2S }] = useMutation(mutation, {
    onCompleted({ generateT2S }) {
      window.open(generateT2S, "_blank");
    },
    onError(error) {
      alerts.error("We encountered an error generating T2S", error);
    },
  });

  const generateT2S = useCallback(
    async () =>
      await mutate({ variables: { reportId: data?.tierIIReport.id ?? "" } }),
    [data?.tierIIReport.id, mutate]
  );

  const shouldDisableT2SDownload =
    data?.tierIIReport.organizationStatus !== TierIiReportOrgStatus.Verified;

  const handleDownloadMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setDownloadMenuAnchor(event.currentTarget);
  };

  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchor(null);
  };

  const handleT2SDownload = () => {
    generateT2S();
    handleDownloadMenuClose();
  };

  const credentialsContent = (
    <CredentialsSection>
      <FieldDisplay
        label="Username:"
        value={
          portalCredential?.credential?.username ? (
            <CopyableTypography text={portalCredential?.credential.username} />
          ) : (
            "N/A"
          )
        }
      />
      <FieldDisplay
        label="Password:"
        value={
          portalCredential?.credential?.password ? (
            <Tooltip title="Click to copy" placement="top">
              <Typography
                variant="body1"
                onClick={() =>
                  navigator.clipboard.writeText(
                    portalCredential?.credential?.password || ""
                  )
                }
                sx={{ cursor: "pointer", display: "inline-block" }}
              >
                ********
              </Typography>
            </Tooltip>
          ) : (
            "N/A"
          )
        }
      />
      <FieldDisplay
        label="URL:"
        value={
          portalCredential?.url ? (
            <CopyableTypography
              text={portalCredential.url}
              sx={{
                display: "inline-block",
                maxWidth: "140px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            />
          ) : (
            "N/A"
          )
        }
      />
      {data?.tierIIReport.facility.state === "WV" && (
        <FieldDisplay
          label="Uses Combined Invoicing:"
          value={
            portalCredential?.credential?.usesCombinedInvoicing ? "Yes" : "No"
          }
        />
      )}
    </CredentialsSection>
  );

  const verificationContent = portalCredential && (
    <VerificationSection>
      <CredentialVerification
        credential={portalCredential.credential}
        facilityId={data.tierIIReport.facility.id ?? ""}
      />
    </VerificationSection>
  );

  // Just the verification status field for collapsed mode
  const verificationStatusOnly = portalCredential?.credential && (
    <FieldDisplay
      label="Verification Status:"
      value={prettyPrintEnumValue(
        portalCredential.credential.verificationStatus
      )}
      noWrap
    />
  );

  const actionButtons = (
    <Stack direction="row" spacing={1} data-testid="action-buttons">
      <EncampyPortalLoginLauncher
        portalCredential={portalCredential}
        facilityAlternateIds={facilityAlternateIds}
        reportingYear={reportingYear}
        reportKind={reportKind}
        onSuccess={(message) => alerts.success(message)}
        onError={(error) => alerts.error(error)}
        size="medium"
        disabled={
          !portalCredential?.credential?.username ||
          !portalCredential?.credential?.password
        }
        disabledReason={
          !portalCredential?.credential?.username ||
          !portalCredential?.credential?.password
            ? "Portal credentials are required for portal login"
            : undefined
        }
      />
      <Button
        onClick={handleDownloadMenuOpen}
        startIcon={<Download sx={{ fontSize: 18 }} />}
        endIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
        size="small"
      >
        Download Options
      </Button>
      <Menu
        anchorEl={downloadMenuAnchor}
        open={Boolean(downloadMenuAnchor)}
        onClose={handleDownloadMenuClose}
      >
        <MenuItem
          onClick={handleT2SDownload}
          disabled={shouldDisableT2SDownload || isGeneratingT2S}
        >
          <Download sx={{ marginRight: theme.spacing(1), fontSize: 18 }} />
          Tier2Submit
        </MenuItem>
        <MenuItem>
          <GeneratePdfButton
            startIcon={
              <Download sx={{ marginRight: theme.spacing(1), fontSize: 18 }} />
            }
            label="Generate PDF"
            reportId={data?.tierIIReport.id ?? ""}
            disabled={!data?.tierIIReport.id?.length}
            variant="text"
            sx={{ padding: 0, minWidth: "auto" }}
            onComplete={handleDownloadMenuClose}
          />
        </MenuItem>
      </Menu>
    </Stack>
  );

  return (
    <Stack
      direction="column"
      component={Paper}
      elevation={0}
      spacing={2}
      padding={theme.spacing(2, 3, 2, 3)}
    >
      {/* Header with title and expand/collapse button */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
          }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Typography variant="h6" data-testid="state-portal-title">
            State Portal
          </Typography>
          <IconButton size="small">
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Stack>

      {/* Show only verification status when collapsed */}
      {!isExpanded && verificationStatusOnly && (
        <Box sx={{ pl: 0 }}>{verificationStatusOnly}</Box>
      )}

      <Collapse in={isExpanded}>
        <Grid container spacing={3}>
          {/* Credentials content */}
          <Grid size={12}>{credentialsContent}</Grid>

          {/* Verification content */}
          <Grid size={12}>{verificationContent}</Grid>

          {/* Action buttons at bottom */}
          <Grid size={12}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              {actionButtons}
            </Box>
          </Grid>
        </Grid>
      </Collapse>
    </Stack>
  );
}
